import React from 'react';
import './PurchaseCompletePage.css';
import FeedbackModal from '../feedback/FeedbackModal';
import { useAuth } from '../../contexts/AuthContext';

interface TransactionData {
  transactionId: string;
  customerInfo: {
    fullName: string;
    email: string;
    phone: string;
  };
  workspaceInfo: {
    title: string;
    location: string;
    price: number;
  };
  billingInfo: {
    startDate: string;
    endDate: string;
    amount: number;
    tax: number;
    total: number;
  };
  paymentDate: string;
}

interface PurchaseCompletePageProps {
  transactionData: TransactionData;
  onBackToHome: () => void;
}

export const PurchaseCompletePage: React.FC<PurchaseCompletePageProps> = ({ 
  transactionData, 
  onBackToHome 
}) => {
  const [showFeedback, setShowFeedback] = React.useState(false);
  const { user } = useAuth();

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowFeedback(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleBackClick = () => {
    if (user) {
      // Logged in user - navigate to account dashboard
      window.history.pushState({ page: 'account' }, '');
    }
    onBackToHome();
  };

  return (
    <div className="purchase-complete-page">
      <div className="success-container">
        {/* Success Header */}
        <div className="success-header">
          <div className="success-icon">✓</div>
          <h1>Payment Successful!</h1>
          <p>Your workspace booking has been confirmed</p>
        </div>

        {/* Transaction Details Card */}
        <div className="transaction-card">
          <div className="transaction-header">
            <h2>Transaction Details</h2>
            <div className="transaction-id">
              <span>Transaction ID: {transactionData.transactionId}</span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="detail-section">
            <h3>Customer Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">Name:</span>
                <span className="value">{transactionData.customerInfo.fullName}</span>
              </div>
              <div className="detail-item">
                <span className="label">Email:</span>
                <span className="value">{transactionData.customerInfo.email}</span>
              </div>
              <div className="detail-item">
                <span className="label">Phone:</span>
                <span className="value">{transactionData.customerInfo.phone}</span>
              </div>
            </div>
          </div>

          {/* Workspace Information */}
          <div className="detail-section">
            <h3>Workspace Details</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">Workspace:</span>
                <span className="value">{transactionData.workspaceInfo.title}</span>
              </div>
              <div className="detail-item">
                <span className="label">Location:</span>
                <span className="value">{transactionData.workspaceInfo.location}</span>
              </div>
              <div className="detail-item">
                <span className="label">Monthly Rate:</span>
                <span className="value">₹{transactionData.workspaceInfo.price.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Billing Information */}
          <div className="detail-section">
            <h3>Billing Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">Billing Period:</span>
                <span className="value">{transactionData.billingInfo.startDate} - {transactionData.billingInfo.endDate}</span>
              </div>
              <div className="detail-item">
                <span className="label">Payment Date:</span>
                <span className="value">{transactionData.paymentDate}</span>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="detail-section">
            <h3>Payment Breakdown</h3>
            <div className="payment-breakdown">
              <div className="breakdown-row">
                <span>Monthly Amount</span>
                <span>₹{transactionData.billingInfo.amount.toLocaleString()}</span>
              </div>
              <div className="breakdown-row">
                <span>Tax (18%)</span>
                <span>₹{transactionData.billingInfo.tax.toLocaleString()}</span>
              </div>
              <div className="breakdown-row total">
                <span>Total Paid</span>
                <span>₹{transactionData.billingInfo.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="home-btn" onClick={handleBackClick}>
            <span>{user ? 'Back to Dashboard' : 'Back to Home'}</span>
          </button>
        </div>
      </div>
      
      <FeedbackModal
        bookingId="dummy-booking-id"
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
    </div>
  );
};